import { DIRECTION_NAMES, STARTER_WORN, defaultAppearance, fallbackNumber, isBarePiece, lookByNumber, lookOf, lookSheetIds, newWireLook, settleWireLook, wireLookOf, type DirectionName, type LookComposeLayer, type LookLayout, type LookSlot, type Looks, type SheetAnim, type WireLook } from "odyssey-shared";

/**
 * How a look becomes a block (ADR-094 decisions 14 to 16), as pure functions the look cache draws by: a look's key,
 * the sheets a look is composed from, where a block lies on its look page, what a block's frames
 * are called, and the strips a composed character plays.
 */

/**
 * The slots of a wire look, in the order its key lists them.
 */
const KEY_SLOTS = ["skin", "hair", "outfit", "hat", "mainHand", "offHand"] as const satisfies readonly LookSlot[];

/**
 * The slots whose piece nobody is drawn without: a skin or an outfit of 0 is drawn as the first of its slot.
 */
const NEEDED_SLOTS: ReadonlySet<LookSlot> = new Set<LookSlot>(["skin", "outfit"]);

/**
 * A look's key in the cache: its six piece numbers and its flags. Two characters whose keys are equal share a block.
 * Made when a look arrives, never in a frame.
 *
 * @param look The look, settled against the page's table (`settleWireLook`), so that two numbers the page draws alike
 *   are one key.
 * @returns The key.
 * @example
 * lookKey({ skin: 3, hair: 17, flags: 0, outfit: 38, hat: 0, mainHand: 57, offHand: 58 }); // "3.17.38.0.57.58.0"
 */
export function lookKey(look: Readonly<WireLook>): string {
  return `${look.skin}.${look.hair}.${look.outfit}.${look.hat}.${look.mainHand}.${look.offHand}.${look.flags}`;
}

/**
 * The look a crowd creature is drawn in under the developer flag (P2.10-T5): the default look of its handle with the
 * starter tools, so a crowd wears many looks with nothing on the wire. The same handle wears the same look on every
 * page of one build.
 *
 * @param looks The page's looks table.
 * @param handle The creature's handle.
 * @param out Where the look is written.
 * @returns `out`.
 */
export function crowdLook(looks: Looks, handle: number, out: WireLook): WireLook {
  wireLookOf(looks, lookOf(defaultAppearance(looks, `crowd:${handle}`), STARTER_WORN), out);
  return out;
}

/**
 * The sheets a look is composed from, bottom first, and how many of its slots fell back.
 */
export interface ComposePlan {
  readonly sheets: string[];
  fellBack: number;
}

/**
 * An empty plan, for `composeSheets` to fill.
 *
 * @returns The plan.
 */
export function newComposePlan(): ComposePlan {
  return { sheets: [], fellBack: 0 };
}

/**
 * Whether the page's sheet pointer names every sheet of a piece.
 *
 * @param sheets The pointer's sheets, by sheet id.
 * @param slot The piece's slot.
 * @param key The piece's key.
 * @returns True when no sheet is missing.
 */
function hasSheets(sheets: Readonly<Record<string, string>>, slot: LookSlot, key: string): boolean {
  return lookSheetIds(slot, key).every((id) => Object.hasOwn(sheets, id));
}

/**
 * The piece a slot is drawn with: the look's, or the slot's fallback where the look names nothing drawable.
 *
 * @param looks The page's looks table.
 * @param sheets The pointer's sheets, by sheet id.
 * @param slot The slot.
 * @param number The slot's piece number in the settled look.
 * @param plan The plan, whose count of slots that fell back is raised when this one does.
 * @returns The piece's key, or null for a slot drawn empty: one the look leaves empty, or one whose piece is bare
 *   (`isBarePiece`: "no hair"), for which nothing is drawn, no sheet exists and no fallback is counted.
 */
function pieceFor(looks: Looks, sheets: Readonly<Record<string, string>>, slot: LookSlot, number: number, plan: ComposePlan): string | null {
  const asked = number === 0 ? undefined : lookByNumber(looks, number);
  if (asked !== undefined && isBarePiece(asked)) {
    return null;
  }
  if (asked !== undefined && hasSheets(sheets, slot, asked.key)) {
    return asked.key;
  }
  if (number === 0 && !NEEDED_SLOTS.has(slot)) {
    return null;
  }
  plan.fellBack += 1;
  const fallback = lookByNumber(looks, fallbackNumber(looks, slot));
  return fallback !== undefined && hasSheets(sheets, slot, fallback.key) ? fallback.key : null;
}

/**
 * The sheets a look is composed from, in the layout's drawing order (ADR-094 decision 14: tools behind, skin, outfit,
 * hair, hat, tools in front). What the page cannot draw as asked draws the slot's fallback (decision 10): a number
 * past the page's table or naming a piece of another slot, a skin or an outfit of 0, and a piece the pointer the page
 * loaded names no sheet for; the fallback is the first skin and the first clothes, and nothing for hair, a hat and the
 * hands. A fallback the pointer lacks too is left out.
 *
 * @param looks The page's looks table.
 * @param compose The layout's drawing order.
 * @param sheets The pointer's sheets, by sheet id.
 * @param look The look as it arrived.
 * @param plan Where the sheet ids are written, replacing what it held, with how many slots fell back.
 * @returns `plan`.
 */
export function composeSheets(looks: Looks, compose: readonly LookComposeLayer[], sheets: Readonly<Record<string, string>>, look: Readonly<WireLook>, plan: ComposePlan): ComposePlan {
  const settled = newWireLook();
  plan.sheets.length = 0;
  plan.fellBack = settleWireLook(looks, look, settled);
  const pieces: Partial<Record<LookSlot, string | null>> = {};
  for (const slot of KEY_SLOTS) {
    pieces[slot] = pieceFor(looks, sheets, slot, settled[slot], plan);
  }
  for (const layer of compose) {
    const key = pieces[layer.slot];
    if (key !== null && key !== undefined) {
      plan.sheets.push(layer.rank === null ? key : `${key}.${layer.rank}`);
    }
  }
  return plan;
}

/**
 * How many whole blocks one look page holds.
 *
 * @param block A block's size in pixels.
 * @param pageSize A page's side in pixels.
 * @returns The count: 24 for 512 by 320 on 2048; 0 when a block does not fit.
 */
export function blocksPerPage(block: { readonly width: number; readonly height: number }, pageSize: number): number {
  return Math.floor(pageSize / block.width) * Math.floor(pageSize / block.height);
}

/**
 * A page's texture memory.
 *
 * @param pageSize A page's side in pixels.
 * @returns MiB at four bytes a pixel: 16 for 2048.
 */
export function pagePixelsMiB(pageSize: number): number {
  return (pageSize * pageSize * 4) / (1024 * 1024);
}

/**
 * Where a block lies: its page, its place among that page's blocks, and its top left corner on the page in pixels.
 */
export interface BlockPlace {
  page: number;
  index: number;
  x: number;
  y: number;
}

/**
 * A place for `blockPlace` to fill.
 *
 * @returns The place.
 */
export function newBlockPlace(): BlockPlace {
  return { page: 0, index: 0, x: 0, y: 0 };
}

/**
 * Where a block lies on the look pages: blocks fill a page row by row, then the next page.
 *
 * @param block The block's number.
 * @param size A block's size in pixels.
 * @param pageSize A page's side in pixels.
 * @param out Where the place is written.
 * @returns `out`.
 * @throws RangeError when the block is no whole number from 0, or no block fits a page.
 */
export function blockPlace(block: number, size: { readonly width: number; readonly height: number }, pageSize: number, out: BlockPlace): BlockPlace {
  const across = Math.floor(pageSize / size.width);
  const each = across * Math.floor(pageSize / size.height);
  if (!Number.isInteger(block) || block < 0 || each === 0) {
    throw new RangeError(`no block ${block} on look pages of ${pageSize} holding ${each} blocks`);
  }
  out.page = Math.floor(block / each);
  out.index = block % each;
  out.x = (out.index % across) * size.width;
  out.y = Math.floor(out.index / across) * size.height;
  return out;
}

/**
 * The name of a block's frame on its page: a whole number, so showing a frame builds no string.
 *
 * @param index The block's place among its page's blocks.
 * @param cell The cell, its place in the layout's `cells`.
 * @param cells How many cells a block has.
 * @returns The frame's name.
 */
export function lookFrameName(index: number, cell: number, cells: number): number {
  return index * cells + cell;
}

/**
 * The strips a composed character plays: each strip's rate and length as the playback reads them (`anims`, whose
 * `files` hold the strip's name by direction for the report, since a composed strip has no file), and the cell of
 * each frame by strip and direction (`cells`).
 */
export interface LookStrips {
  readonly anims: Readonly<Record<string, SheetAnim>>;
  readonly cells: Readonly<Record<string, Readonly<Record<DirectionName, readonly number[]>>>>;
}

/**
 * The strips of a layout, made once and shared by every composed character.
 *
 * @param layout The look layout.
 * @returns The strips. A strip's length is its shortest direction's, so no direction is asked for a frame it lacks.
 */
export function lookStrips(layout: LookLayout): LookStrips {
  const anims: Record<string, SheetAnim> = {};
  const cells: Record<string, Readonly<Record<DirectionName, readonly number[]>>> = {};
  for (const [name, strip] of Object.entries(layout.strips)) {
    const files = {} as Record<DirectionName, string>;
    let frames = Number.POSITIVE_INFINITY;
    for (const direction of DIRECTION_NAMES) {
      files[direction] = `look:${name}:${direction}`;
      frames = Math.min(frames, strip.frames[direction].length);
    }
    anims[name] = { fps: strip.fps, frames, files };
    cells[name] = strip.frames;
  }
  return { anims, cells };
}
